import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/dramas';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/category/all?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-movie-line text-lg md:text-xl text-white"></i>
            </div>
            <span className="text-lg md:text-xl font-heading font-bold text-foreground-900 whitespace-nowrap">
              久久影视
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-3 py-2 text-sm rounded-lg transition-colors duration-200 whitespace-nowrap ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索剧集..."
                className="w-44 lg:w-56 h-9 pl-9 pr-3 text-sm rounded-full bg-background-100 border border-background-300 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
              <button
                type="submit"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-search-line text-foreground-400 text-sm"></i>
              </button>
            </form>

            <Link
              to="/about"
              className="px-4 py-2 text-sm text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap"
            >
              关于我们
            </Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
            aria-label="菜单"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            <form onSubmit={handleSearch} className="relative mb-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索剧集..."
                className="w-full h-10 pl-9 pr-3 text-sm rounded-full bg-background-100 border border-background-300 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
              />
              <button
                type="submit"
                className="absolute left-3 top-1/2 -translate-y-1/2 cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-search-line text-foreground-400 text-sm"></i>
              </button>
            </form>

            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </Link>
            ))}

            <Link
              to="/about"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-200 transition-colors"
            >
              <i className="ri-information-line text-base"></i>
              关于我们
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}